import { useCallback, useEffect, useRef, useState } from "react";

import { toast } from "@/components/ui/toast";
import { messages, SendMessageError } from "@/lib/api";
import type { Conversation } from "@/types";

export type PendingStatus = "sending" | "sent" | "failed";

export interface PendingMessage {
  tempId: string;
  content: string;
  createdAt: string;
  status: PendingStatus;
  errorCode?: string;
  errorMessage?: string;
  isTransient?: boolean;
  messageIdFromServer?: string;
}

interface UseSendMessageArgs {
  leadId: string;
  token: string | undefined;
  conversation: Conversation;
  onAutoTakeOver?: () => void;
  onSent?: () => void;
}

const STRUCTURAL_ERRORS = new Set([
  "ventana_24h_cerrada",
  "lead_bloqueado",
  "lead_not_found",
  "mensaje_muy_largo",
  "content_required",
]);

const MAX_RECONCILE_AGE_MS = 60_000;
const SETTLE_GLOW_MS = 800;
const FAILURE_GRACE_MS = 30_000;

function mapErrorToBubbleText(err: SendMessageError): string {
  switch (err.code) {
    case "ventana_24h_cerrada":
      return "Ventana de WhatsApp cerrada (>24h)";
    case "lead_bloqueado":
      return "El lead bloqueó el bot";
    case "mensaje_muy_largo": {
      const actual = err.detail.actual ?? "?";
      const max = err.detail.max ?? "?";
      return `Mensaje muy largo (${actual}/${max})`;
    }
    case "telegram_error":
    case "whatsapp_error":
      return "Error de envío. Reintentá.";
    case "lead_not_found":
      return "Lead no encontrado";
    case "content_required":
      return "Mensaje vacío";
    default:
      return "Error de envío";
  }
}

function mapErrorToToastText(err: SendMessageError): string {
  switch (err.code) {
    case "ventana_24h_cerrada":
      return "Ventana de WhatsApp cerrada";
    case "lead_bloqueado":
      return "El lead bloqueó el bot";
    case "mensaje_muy_largo":
      return "Mensaje muy largo";
    case "telegram_error":
    case "whatsapp_error":
      return "Error del canal";
    case "lead_not_found":
      return "Lead no encontrado";
    default:
      return "No pudimos enviar el mensaje";
  }
}

function makeTempId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `pending_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

export function useSendMessage({
  leadId,
  token,
  conversation,
  onAutoTakeOver,
  onSent,
}: UseSendMessageArgs) {
  const [pending, setPending] = useState<PendingMessage[]>([]);
  const matchedServerIdsRef = useRef<Set<string>>(new Set());
  const failureTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map()
  );
  const removalTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map()
  );

  // Cleanup todos los timers en unmount (evita setState en componente desmontado).
  useEffect(() => {
    return () => {
      failureTimersRef.current.forEach((t) => clearTimeout(t));
      removalTimersRef.current.forEach((t) => clearTimeout(t));
      failureTimersRef.current.clear();
      removalTimersRef.current.clear();
    };
  }, []);

  const send = useCallback(
    async (content: string) => {
      const tempId = makeTempId();
      const createdAt = new Date().toISOString();

      setPending((prev) => [
        ...prev,
        { tempId, content, createdAt, status: "sending" },
      ]);

      try {
        const res = await messages.send({
          leadId,
          token,
          type: "text",
          content,
        });
        // Defensive: si por alguna razón hubo grace timer armado, cancelar.
        const t = failureTimersRef.current.get(tempId);
        if (t) {
          clearTimeout(t);
          failureTimersRef.current.delete(tempId);
        }
        setPending((prev) =>
          prev.map((p) =>
            p.tempId === tempId
              ? { ...p, status: "sent", messageIdFromServer: res.message_id }
              : p
          )
        );
        // Side effects post-success aislados: un throw acá NO debe marcar el
        // bubble como failed — el send REAL fue OK y ya quedó 'sent'.
        try {
          if (res.auto_take_over) {
            toast.info("Tomaste la conversación de Allison", {
              id: `take-over-${leadId}`,
            });
            onAutoTakeOver?.();
          }
          onSent?.();
        } catch (sideEffectErr) {
          console.error(
            "[useSendMessage] post-send side effect failed",
            sideEffectErr
          );
        }
      } catch (err) {
        const isSendErr = err instanceof SendMessageError;
        const bubbleMsg = isSendErr
          ? mapErrorToBubbleText(err)
          : "Error de envío";
        const toastMsg = isSendErr
          ? mapErrorToToastText(err)
          : "No pudimos enviar el mensaje";
        const errorCode = isSendErr ? err.code : "unknown";
        const isTransient = !STRUCTURAL_ERRORS.has(errorCode);

        // Grace period 30s: el mensaje pudo persistir server-side aunque el
        // cliente recibió error (timeout, CORS transitorio, etc). Si reconcile
        // matchea con un server message dentro de 30s, este timer se cancela
        // y el bubble pasa a 'sent'. Sino, marca failed después.
        const failTimer = setTimeout(() => {
          failureTimersRef.current.delete(tempId);
          setPending((prev) =>
            prev.map((p) =>
              p.tempId === tempId && p.status === "sending"
                ? {
                    ...p,
                    status: "failed",
                    errorCode,
                    errorMessage: bubbleMsg,
                    isTransient,
                  }
                : p
            )
          );
          toast.error(toastMsg, { id: "send-error" });
        }, FAILURE_GRACE_MS);
        failureTimersRef.current.set(tempId, failTimer);
      }
    },
    [leadId, token, onAutoTakeOver, onSent]
  );

  const retry = useCallback(
    (tempId: string) => {
      const t = failureTimersRef.current.get(tempId);
      if (t) {
        clearTimeout(t);
        failureTimersRef.current.delete(tempId);
      }
      const msg = pending.find((p) => p.tempId === tempId);
      if (!msg) return;
      setPending((prev) => prev.filter((p) => p.tempId !== tempId));
      send(msg.content);
    },
    [pending, send]
  );

  const dismiss = useCallback((tempId: string) => {
    const failT = failureTimersRef.current.get(tempId);
    if (failT) {
      clearTimeout(failT);
      failureTimersRef.current.delete(tempId);
    }
    const remT = removalTimersRef.current.get(tempId);
    if (remT) {
      clearTimeout(remT);
      removalTimersRef.current.delete(tempId);
    }
    setPending((prev) => prev.filter((p) => p.tempId !== tempId));
  }, []);

  // Reconciliación FIFO con server messages contra TODOS los pending
  // (sending/sent/failed). El server message es señal definitiva de éxito —
  // upgradear a 'sent', cancelar grace timer si armado, schedule removal.
  useEffect(() => {
    if (pending.length === 0) return;

    const cutoff = Date.now() - MAX_RECONCILE_AGE_MS;
    const candidates = conversation.messages
      .filter(
        (m) =>
          m.role === "agent" &&
          m.source !== "allison" &&
          new Date(m.createdAt).getTime() >= cutoff &&
          !matchedServerIdsRef.current.has(m.id)
      )
      .sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );

    if (candidates.length === 0) return;

    const sortedPending = [...pending].sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );

    const tempIdsToReconcile = new Set<string>();
    const matchCount = Math.min(sortedPending.length, candidates.length);
    for (let i = 0; i < matchCount; i++) {
      const tempId = sortedPending[i].tempId;
      tempIdsToReconcile.add(tempId);
      matchedServerIdsRef.current.add(candidates[i].id);
      // Cancel grace timer if armado — el mensaje sí persistió.
      const failT = failureTimersRef.current.get(tempId);
      if (failT) {
        clearTimeout(failT);
        failureTimersRef.current.delete(tempId);
      }
    }

    if (tempIdsToReconcile.size === 0) return;

    // Mark matched as 'sent' solo si cambió algo (evita re-render innecesario).
    setPending((prev) => {
      let changed = false;
      const next = prev.map((p) => {
        if (tempIdsToReconcile.has(p.tempId) && p.status !== "sent") {
          changed = true;
          return {
            ...p,
            status: "sent" as PendingStatus,
            errorCode: undefined,
            errorMessage: undefined,
            isTransient: undefined,
          };
        }
        return p;
      });
      return changed ? next : prev;
    });

    // Schedule removal en su propio ref (no en cleanup del effect — un
    // re-render lo cancelaría antes de que dispare).
    for (const tempId of tempIdsToReconcile) {
      if (removalTimersRef.current.has(tempId)) continue;
      const removal = setTimeout(() => {
        removalTimersRef.current.delete(tempId);
        setPending((prev) => prev.filter((p) => p.tempId !== tempId));
      }, SETTLE_GLOW_MS);
      removalTimersRef.current.set(tempId, removal);
    }
  }, [conversation.messages, pending]);

  return { pending, send, retry, dismiss };
}
